import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Subject, map } from "rxjs";
import { DemoAssetMediaModel, DemoTileViewModel, SuggestedBehavior, SuggestedDemo } from "src/app/core/model/ContentManagement";
import { DemoAssetService } from "src/app/services/demo-asset.service";
import { DemoPopupComponent } from "../demo-popup/demo-popup.component";
import { CollectionService } from "src/app/services/collection.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

export enum DemoTileStatusEnum {
  default = "default",
  added = "added",
  suggested = "suggested",
  disabeld = "disabeld",
  markAsDefault = "markasdefault",
  news = "news"
}

@Component({
  selector: "brd-demo-tile",
  templateUrl: "./demo-tile.component.html",
  styleUrl: "./demo-tile.component.less",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class DemoTileComponent implements OnInit, OnChanges {
  @Input() asset!: DemoTileViewModel;
  @Input() remainingCapacity: {
    ramConsumption: number;
    diskConsumption: number;
  };
  @Input() collectionIds: number[];
  @Input() suggested: SuggestedDemo[];
  @Input() instancesQuotaExceeded!: boolean;

  cardHover = false;
  cardStatus = signal(DemoTileStatusEnum.default);
  cardStatuses = DemoTileStatusEnum;
  cartIsEmpty = signal(true);

  private mediaSubject = new Subject<DemoAssetMediaModel | undefined>();
  mediaObs$ = this.mediaSubject.asObservable();

  destroyRef = inject(DestroyRef);

  showButton = computed(() => {
    if (this.cardStatus() !== DemoTileStatusEnum.markAsDefault) return true;
    if (this.collectionIds?.includes(this.asset.id)) return true;

    if (this.cartIsEmpty()) return true;
    return false;
  });

  constructor(
    private demoAssetService: DemoAssetService,
    private collectionService: CollectionService,
    private readonly dialog: MatDialog
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const remainingCapacity: {
      ramConsumption: number;
      diskConsumption: number;
    } = changes?.remainingCapacity?.currentValue ?? this.remainingCapacity;
    const suggestedDemos: SuggestedDemo[] = changes?.suggested?.currentValue ?? this.suggested;
    const totalRamConsumption = this.asset.suggestedDemos.reduce(
      (acc, sugg) =>
        acc +
        (sugg.behavior === SuggestedBehavior.default && !this.collectionIds?.includes(sugg.suggestedId) ? sugg.suggestedRamConsumption : 0),
      this.asset.ramConsumption
    );
    const totalDiskConsumption = this.asset.suggestedDemos.reduce(
      (acc, sugg) =>
        acc +
        (sugg.behavior === SuggestedBehavior.default && !this.collectionIds?.includes(sugg.suggestedId)
          ? sugg.suggestedDiskConsumption
          : 0),
      this.asset.diskConsumption
    );
    const suggestedDemo = suggestedDemos.find(x => x.suggestedId === this.asset.id);

    if (changes.collectionIds?.currentValue.includes(this.asset.id)) this.cardStatus.set(DemoTileStatusEnum.added);
    else if (this.asset.default || suggestedDemo?.behavior === SuggestedBehavior.default) {
      this.cardStatus.set(DemoTileStatusEnum.markAsDefault);
    } else if (
      remainingCapacity
        ? remainingCapacity.ramConsumption < totalRamConsumption || remainingCapacity.diskConsumption < totalDiskConsumption
        : false
    )
      this.cardStatus.set(DemoTileStatusEnum.disabeld);
    else if (!!suggestedDemo && suggestedDemo.behavior === SuggestedBehavior.suggested) {
      this.cardStatus.set(DemoTileStatusEnum.suggested);
    } else if (this.asset.newsCategoryName) this.cardStatus.set(DemoTileStatusEnum.news);
    else this.cardStatus.set(DemoTileStatusEnum.default);

    if (changes?.collectionIds) this.cartIsEmpty.set(changes.collectionIds.currentValue.length === 0);
  }

  ngOnInit(): void {
    this.demoAssetService
      .getAssetMedia(this.asset.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(res => {
          this.mediaSubject.next(res);
        })
      )
      .subscribe();
  }

  infoButtonClick(event: Event, startFullScreen = false): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(DemoPopupComponent, {
      width: "80vw",
      height: "90vh",
      maxWidth: "1250px",
      maxHeight: "700px",
      data: {
        demo: this.asset,
        canAddToCollection:
          this.cardStatus() === DemoTileStatusEnum.default ||
          this.cardStatus() === DemoTileStatusEnum.suggested ||
          (this.cardStatus() === DemoTileStatusEnum.markAsDefault && this.cartIsEmpty()),
        startFullScreen
      }
    });

    dialogRef.afterClosed().subscribe();
  }

  buttonClick(event: Event): void {
    event.stopPropagation();

    switch (this.cardStatus()) {
      case DemoTileStatusEnum.added:
        this.collectionService.removeFromCollection(this.asset);
        break;
      case DemoTileStatusEnum.suggested:
      case DemoTileStatusEnum.default:
      case DemoTileStatusEnum.news:
        this.collectionService.addToCollection(this.asset);
        break;
      case DemoTileStatusEnum.markAsDefault:
        if (this.cartIsEmpty()) this.collectionService.addToCollection(this.asset);
        break;
      default:
        break;
    }
  }
}
